import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { VersionSelector } from './VersionSelector.js';
import { PendingImagesDrawer } from './PendingImagesDrawer.js';
import { useProjectVersions, usePendingImages } from '../../hooks/useProjects.js';
import { useAllSettings } from '../../hooks/useSettings.js';
import { type ProjectRow } from '../../hooks/useProjects.js';

interface Props {
  project: ProjectRow;
}

export function PagePreview({ project }: Props) {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [selectedVersion, setSelectedVersion] = useState(project.currentVersion);
  const { data: versionsData } = useProjectVersions(project.id);

  // Sync selectedVersion when a new version is generated
  useEffect(() => {
    setSelectedVersion(project.currentVersion);
  }, [project.currentVersion]);

  const isViewingOld = selectedVersion < project.currentVersion;

  // Derive pending count for the drawer trigger badge.
  // React Query dedupes this against the same fetch inside PendingImagesList.
  const { data: pendingData, isLoading: isPendingLoading } = usePendingImages(
    project.id,
    isViewingOld ? selectedVersion : undefined,
  );
  const pendingCount = pendingData
    ? pendingData.images.filter(img => !img.uploaded).length
    : 0;

  // AI image generation — enabled when a non-'none' provider is configured
  const { data: appSettings } = useAllSettings();
  const aiEnabled = (appSettings?.['image_generation.provider'] ?? 'none') !== 'none';

  // Build the preview URL — append ?v=N only for historical versions
  const previewBase = `/api/projects/${project.id}/preview/index.html`;
  const previewUrl = isViewingOld ? `${previewBase}?v=${selectedVersion}` : previewBase;

  function reloadIframe() {
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.src = previewUrl;
    }
  }

  function handleVersionSelect(v: number) {
    // key={previewUrl} on the iframe already forces a remount when the URL changes
    setSelectedVersion(v);
  }

  return (
    // Outer shell: position:relative so DrawerSurface's position:absolute children
    // reference this container. NO padding here — padding lives in the inner wrapper
    // so the backdrop (inset:0) covers the full panel without a residual frame (IMP-4).
    <div style={{ position: 'relative' }}>
      <div style={{ padding: '32px var(--evo-content-pad-x)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-5)' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-4)', flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.preview.title')}
          </span>
          {versionsData && (
            <VersionSelector
              versions={versionsData.versions}
              currentVersion={project.currentVersion}
              selectedVersion={selectedVersion}
              onSelect={handleVersionSelect}
            />
          )}
        </div>
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-cyan)',
            fontFamily: 'var(--evo-font)',
            textDecoration: 'none',
          }}
        >
          {t('projects.preview.openInTab')}
        </a>
      </div>

      {/* Old-version banner */}
      {isViewingOld && (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          border: '1px solid var(--evo-stroke)',
          borderRadius: 'var(--evo-r-sm)',
          background: 'var(--evo-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--evo-space-4)',
        }}>
          <span style={{
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.versions.viewingOld', {
              version: selectedVersion,
              current: project.currentVersion,
            })}
          </span>
          <Button variant="link" tone="cyan" size="sm" onClick={() => handleVersionSelect(project.currentVersion)}>
            {t('projects.versions.goToCurrent')}
          </Button>
        </div>
      )}

      {/* Iframe */}
      <div style={{
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        overflow: 'hidden',
      }}>
        <iframe
          key={previewUrl}
          ref={iframeRef}
          src={previewUrl}
          style={{
            width: '100%',
            height: '600px',
            border: 'none',
            display: 'block',
          }}
          title={t('projects.preview.title')}
        />
      </div>

      {/* Pending images drawer — sticky trigger + bottom panel */}
      <PendingImagesDrawer
        projectId={project.id}
        {...(isViewingOld ? { version: selectedVersion } : {})}
        pendingCount={pendingCount}
        isPendingLoading={isPendingLoading}
        onUploaded={reloadIframe}
        aiEnabled={aiEnabled}
        projectStatus={project.status}
      />
      </div>
    </div>
  );
}
